import { apiClient, ApiError } from './api'
import type { Profile } from './types'

export class ProfileService {
  /**
   * Fetch user profile information by profile ID
   */
  async getProfile(profileId: string): Promise<Profile> {
    try {
      const response = await apiClient.get<Profile>(
        `/functions/v1/profile?profile_id=${profileId}`
      )
      return response
    } catch (error) {
      console.error('Failed to fetch profile:', error)
      throw new Error('Failed to fetch profile information')
    }
  }

  /**
   * Fetch the current authenticated user's profile information
   */
  async getCurrentProfile(): Promise<Profile> {
    try {
      const response = await apiClient.get<Profile>(`/functions/v1/profile`)
      return response
    } catch (error) {
      console.error('Failed to fetch current profile:', error)
      if (error instanceof ApiError) {
        throw error
      }
      throw new Error('Failed to fetch current user profile information')
    }
  }

  /**
   * Create a name-only waitlist profile (no photo). The backend creates it as
   * is_waitlist=true by default; the photo is collected later in the app.
   * Requires an authenticated session (phone OTP) like createProfile.
   */
  async createWaitlistProfile(
    firstName: string,
    lastName: string
  ): Promise<{ id: string }> {
    const formData = new FormData()
    formData.append(
      'profile',
      JSON.stringify({ first_name: firstName, last_name: lastName })
    )
    formData.append('allow_no_image', 'true')

    return apiClient.postFormData<{ id: string }>(
      '/functions/v1/profile',
      formData
    )
  }

  /**
   * Create or update profile with photo. Requires authentication.
   */
  async createProfile(
    firstName: string,
    lastName: string,
    imageFile: File
  ): Promise<{ id: string }> {
    const formData = new FormData()
    formData.append(
      'profile',
      JSON.stringify({ first_name: firstName, last_name: lastName })
    )
    formData.append('image', imageFile)

    const response = await apiClient.postFormData<{ id: string }>(
      '/functions/v1/profile',
      formData
    )
    return response
  }
}

export const profileService = new ProfileService()
